import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowLeftIcon, 
  BanknotesIcon, 
  ClockIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MinusIcon
} from '@heroicons/react/24/solid';
import confetti from 'canvas-confetti';
import api from '../api';

export default function BetSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const betId = location.state?.betId;
  const initChips = location.state?.remainingChips ?? 0;

  // Updated status to include 'draw' as a possible outcome
  const [status, setStatus] = useState('pending');
  const [result, setResult] = useState(null); // 'win', 'loss', or 'draw'
  const [chips, setChips] = useState(initChips);
  const [betDetails, setBetDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const [actualPrice, setActualPrice] = useState(null);

  // Trigger confetti on settlement win
  useEffect(() => {
    if (status === 'settled' && result === 'win') {
      const duration = 2.5 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 25, spread: 300, ticks: 50, zIndex: 0 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 40 * (timeLeft / duration);
        
        // Launch confetti from both sides
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ['#8B5CF6', '#EC4899', '#F43F5E', '#10B981']
        });
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ['#8B5CF6', '#EC4899', '#F43F5E', '#10B981']
        });
      }, 250);
    }
  }, [status, result]);

  // Update countdown timer
  useEffect(() => {
    if (!betDetails || status !== 'pending') return;

    const timer = setInterval(() => {
      const now = new Date();
      const settleTime = new Date(betDetails.settleAt);
      const timeLeft = settleTime - now;
      
      if (timeLeft <= 0) {
        setCountdown({ hours: 0, minutes: 0, seconds: 0 });
        clearInterval(timer);
        return;
      }
      
      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      
      setCountdown({ hours, minutes, seconds });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [betDetails, status]);

  // Function to determine appropriate decimal places based on price value
  const getDecimalPlaces = (price) => {
    if (!price) return 2;
    
    // For prices below 1, show more decimal places
    if (price < 0.01) return 8;
    if (price < 0.1) return 6;
    if (price < 1) return 4;
    if (price < 10) return 3;
    return 2;
  };

  // Format price with appropriate decimal places
  const formatPrice = (price) => {
    if (price === null || price === undefined) return "0.00";
    const decimalPlaces = getDecimalPlaces(price);
    return price.toFixed(decimalPlaces);
  };

  // Poll for bet status and details
  useEffect(() => {
    if (!betId) return;
  
    const fetchBet = async () => {
      try {
        const response = await api.get(`/bets/${betId}`);
        const payload = response.data;

        console.log("API response:", payload);
  
        const betObject = payload.bet ?? payload.data ?? payload;
  
        setStatus(payload.status);
        setResult(payload.result); // Should now be 'win', 'loss', or 'draw'
        setChips(payload.remainingChips);
        setBetDetails(betObject);
        
        // Set actual price if available (for settled bets)
        if (payload.status === 'settled' && payload.finalPrice) {
          setActualPrice(payload.finalPrice);
        }
      } catch (err) {
        console.error("❌ [fetchBet] error:", err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchBet();
    const interval = setInterval(fetchBet, 5000);
    return () => clearInterval(interval);
  }, [betId]);  

  if (!betId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="p-4 sm:p-6 border border-red-500 rounded-lg bg-black bg-opacity-60 max-w-xs sm:max-w-md mx-auto">
          <div className="flex items-center mb-2">
            <span className="text-red-500 font-bold">Error</span>
          </div>
          <div className="text-sm text-gray-300">
            Invalid bet data. Please return to market selection.
          </div>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-lg flex items-center"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-2 border-violet-500 rounded-full relative flex items-center justify-center">
            <div className="w-10 h-10 bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 rounded-full animate-pulse" />
            <div className="absolute inset-0 border-t-2 border-violet-500 rounded-full animate-spin" />
          </div>
          <div className="mt-4 font-mono text-sm">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
              Loading Results...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!betDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black text-gray-300">
        <div className="p-4 sm:p-6 border border-yellow-500 rounded-lg bg-black bg-opacity-60 max-w-xs sm:max-w-md mx-auto">
          <div className="flex items-center mb-2">
            <ArrowPathIcon className="h-5 w-5 text-yellow-500 mr-2 animate-spin" />
            <span className="text-yellow-500 font-bold">Loading Data</span>
          </div>
          <div className="text-sm text-gray-300">
            Waiting for bet details to load...
          </div>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-gradient-to-r from-violet-500 to-pink-500 text-white rounded-lg flex items-center"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Format odds display
  const formattedOdds = (odds) => {
    return `${(odds || 0).toFixed(2)}x`;
  };

  // Format countdown for simple display
  const formatSimpleCountdown = () => {
    if (!countdown) return "--:--:--";
    
    const { hours, minutes, seconds } = countdown;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Determine model prediction based on predicted price vs entry price
  const modelPredictionIsUp = (betDetails.predicted_price || 0) >= (betDetails.current_price || 0);
  const percentChange = ((((betDetails.predicted_price || 0) / (betDetails.current_price || 1)) - 1) * 100).toFixed(2);

  // Helper function to determine bet result status message
  const getResultStatusMessage = () => {
    if (status === 'pending') return 'Pending';
    
    switch(result) {
      case 'win': return 'Won';
      case 'lose': return 'Lost';
      case 'draw': return 'Draw';
      default: return 'Settled';
    }
  };

  // Helper function to determine result status color
  const getResultStatusColor = () => {
    if (status === 'pending') return 'text-yellow-400';
    
    switch(result) {
      case 'win': return 'text-green-400';
      case 'loss': return 'text-red-400';
      case 'draw': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  // Helper function to determine bet result title
  const getBetResultTitle = () => {
    if (status === 'pending') return 'Bet In Progress';
    
    switch(result) {
      case 'win': return 'Bet Won';
      case 'loss': return 'Bet Lost';
      case 'draw': return 'Bet Draw';
      default: return 'Bet Settled';
    }
  };

  // Helper to render the outcome explanation
  const renderOutcomeExplanation = () => {
    if (status !== 'settled' || !actualPrice) return null;

    console.log('renderOutcomeExplanation:', { result, status, actualPrice });
  
    const currentPrice = betDetails.current_price || 0;
    const predictedPrice = betDetails.predicted_price || 0;
  
    let explanation = '';
  
    switch (result) {
      case 'win':
        explanation = modelPredictionIsUp
          ? `The final price (${formatPrice(actualPrice)}) was below ${formatPrice(currentPrice)}, contradicting the model's prediction that price would rise to ${formatPrice(predictedPrice)}.`
          : `The final price (${formatPrice(actualPrice)}) was above ${formatPrice(currentPrice)}, contradicting the model's prediction that price would fall to ${formatPrice(predictedPrice)}.`;
        break;
      case 'lose':
        explanation = modelPredictionIsUp
          ? `The final price (${formatPrice(actualPrice)}) was above ${formatPrice(currentPrice)}, confirming the model's prediction that price would rise to ${formatPrice(predictedPrice)}.`
          : `The final price (${formatPrice(actualPrice)}) was below ${formatPrice(currentPrice)}, confirming the model's prediction that price would fall to ${formatPrice(predictedPrice)}.`;
        break;
      case 'draw':
        explanation = modelPredictionIsUp
          ? `The final price (${formatPrice(actualPrice)}) was between ${formatPrice(currentPrice)} and ${formatPrice(predictedPrice)}, resulting in a draw.`
          : `The final price (${formatPrice(actualPrice)}) was between ${formatPrice(predictedPrice)} and ${formatPrice(currentPrice)}, resulting in a draw.`;
        break;
      default:
        console.warn('Unknown result:', result);
    }
  
    return explanation || null;
  };
  

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white font-mono px-4 sm:px-4 pb-12 pt-4 sm:pt-4 -mt-16">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-pink-500 to-red-500 text-center mb-6">
          {getBetResultTitle()}
        </h1>

        {/* Market Card */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
          <div className="p-3 sm:p-4 bg-gradient-to-br from-gray-900/90 to-black/90 rounded-lg">
            {/* Header with Countdown in the center */}
            <div className="relative flex justify-between items-center mb-4">
              {/* Left: Symbol & ID */}
              <div>
                <h3 className="text-lg sm:text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                  {betDetails.symbol}
                </h3>
                <p className="text-xs text-gray-400">
                  ID: <span className="text-white">{(betDetails.id ?? '').toString().substring(0, 8) || '—'}</span>
                </p>
              </div>

              {/* Center: Countdown Timer */}
              {status === 'pending' && countdown && (
                <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center bg-gray-800/50 rounded-full px-3 py-1">
                  <ClockIcon className="h-3 w-3 text-violet-400 mr-1" />
                  <span className="text-xs text-violet-300 font-mono">
                    {formatSimpleCountdown()}
                  </span>
                </div>
              )}

              {/* Right: Prediction Arrow */}
              <div
                className={`p-2 rounded-full ${
                  modelPredictionIsUp
                    ? 'bg-gradient-to-tr from-green-400 to-green-600'
                    : 'bg-gradient-to-tr from-red-400 to-red-600'
                }`}
              >
                {modelPredictionIsUp ? (
                  <ArrowUpIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                ) : (
                  <ArrowDownIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                )}
              </div>
            </div>
            
            {/* Status */}
            <div className="mb-4 px-3 py-2 rounded-lg bg-gray-800/50">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Status</span>
                <span className={`font-medium ${getResultStatusColor()}`}>
                  {getResultStatusMessage()}
                </span>
              </div>
            </div>

            {/* Time Information */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-gray-800/50">
                <div className="text-xs text-gray-400 mb-1">Placed</div>
                <div className="text-sm font-semibold">{new Date(betDetails.createdAt).toLocaleTimeString()}</div>
                <div className="text-xs text-gray-400">{new Date(betDetails.createdAt).toLocaleDateString()}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-800/50">
                <div className="text-xs text-gray-400 mb-1">Settles</div>
                <div className="text-sm font-semibold">{new Date(betDetails.settleAt).toLocaleTimeString()}</div>
                <div className="text-xs text-gray-400">{new Date(betDetails.settleAt).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Prices */}
            <div className="mb-4">
              <div className="flex justify-between mb-2 text-sm">
                <span className="text-gray-400">Entry Price:</span>
                <span className="text-white font-semibold">
                  ${formatPrice(betDetails.current_price)}
                </span>
              </div>
              
              <div className="flex justify-between mb-2 text-sm">
                <span className="text-gray-400">Target Price:</span>
                <span className={`font-semibold ${
                  status === 'pending' 
                    ? 'text-white' 
                    : result === 'win' 
                      ? 'text-green-400' 
                      : result === 'loss'
                        ? 'text-red-400'
                        : 'text-blue-400'
                }`}>
                  ${formatPrice(betDetails.predicted_price)}
                  <span className="ml-1 text-xs">
                    ({modelPredictionIsUp ? '+' : ''}{percentChange}%)
                  </span>
                </span>
              </div>
              
              {/* Final Price (only shown when settled) */}
              {status === 'settled' && actualPrice && (
                <div className="flex justify-between mb-2 text-sm">
                  <span className="text-gray-400">Final Price:</span>
                  <span className={`font-semibold ${
                    result === 'win' 
                      ? 'text-green-400' 
                      : result === 'loss'
                        ? 'text-red-400'
                        : 'text-blue-400'
                  }`}>
                    ${formatPrice(actualPrice)}
                  </span>
                </div>
              )}
              
              <div className="flex flex-col gap-1 text-sm">
                {/* Model Prediction */}
                <div className="flex justify-between">
                  <span className="text-gray-400">Model Prediction:</span>
                  <span className={`font-semibold ${modelPredictionIsUp ? 'text-green-400' : 'text-red-400'}`}>
                    Price will go {modelPredictionIsUp ? 'Up' : 'Down'}
                  </span>
                </div>

                {/* Your Prediction */}
                <div className="flex justify-between">
                  <span className="text-gray-400">Your Prediction:</span>
                  <span className={`font-semibold ${!modelPredictionIsUp ? 'text-green-400' : 'text-red-400'}`}>
                    Price will go {!modelPredictionIsUp ? 'Up' : 'Down'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="relative h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
                {/* Progress Bar */}
                <div 
                  className={`absolute top-0 left-0 h-full ${
                    modelPredictionIsUp
                      ? 'bg-gradient-to-r from-blue-500 to-green-500'
                      : 'bg-gradient-to-r from-blue-500 to-red-500'
                  }`}
                  style={{ width: status === 'pending' ? '50%' : '100%' }}
                ></div>
                
                {/* Start Dot */}
                <div className="absolute top-1/2 left-0 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full shadow-md shadow-blue-500/30"></div>
                
                {/* End Dot */}
                <div 
                  className={`absolute top-1/2 right-0 -translate-y-1/2 w-3 h-3 rounded-full shadow-md ${
                    modelPredictionIsUp
                      ? 'bg-green-500 shadow-green-500/30'
                      : 'bg-red-500 shadow-red-500/30'
                  }`}
                ></div>
                
                {/* Final Price Marker (only shown when settled) */}
                {status === 'settled' && actualPrice && (
                  <div
                    className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-md ${
                      result === 'win'
                        ? 'bg-green-500 shadow-green-500/30'
                        : result === 'loss'
                          ? 'bg-red-500 shadow-red-500/30'
                          : 'bg-blue-500 shadow-blue-500/30'
                    }`}
                    style={{
                      left: `${calculatePositionPercentage(
                        actualPrice,
                        betDetails.current_price || 0,
                        betDetails.predicted_price || 0
                      )}%`
                    }}
                  ></div>
                )}
              </div>
              
              {/* Price Labels */}
              <div className="flex justify-between text-xs">
                <span className="text-blue-400">${formatPrice(betDetails.current_price)}</span>
                <span className={`${
                  modelPredictionIsUp ? 'text-green-400' : 'text-red-400'
                }`}>
                  ${formatPrice(betDetails.predicted_price)}
                </span>
              </div>
            </div>

            {/* Bet Details */}
            <div className="bg-gray-800/50 p-3 rounded-xl mb-4 text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-gray-300">Stake:</span>
                <span className="font-medium text-white">
                  {betDetails.amount || 0} CHIPS
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-300">Odds:</span>
                <span className="text-white font-medium">{formattedOdds(betDetails.odds)}</span>
              </div>
            </div>

            {console.log('Render check:', { status, result })}

            {/* Outcome Explanation (only for settled bets) */}
            {status === 'settled' && (
              <div className="bg-gray-800/30 p-3 rounded-xl mb-4 text-xs text-gray-300 border border-gray-700">
                {renderOutcomeExplanation()}
              </div>
            )}

            {/* Win/Loss Conditions */}
            {status === 'pending' && (
              <div className="bg-gray-800/30 p-3 rounded-xl mb-4 text-xs text-gray-300 border border-gray-700">
                <div className="flex items-center mb-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-400 mr-1" />
                  <span>
                    You win if the final price is 
                    {modelPredictionIsUp 
                      ? ` below $${formatPrice(betDetails.current_price)}` 
                      : ` above $${formatPrice(betDetails.current_price)}`}
                  </span>
                </div>
                <div className="flex items-center mb-2">
                  <XCircleIcon className="h-4 w-4 text-red-400 mr-1" />
                  <span>
                    You lose if the final price is 
                    {modelPredictionIsUp 
                      ? ` above $${formatPrice(betDetails.predicted_price)}` 
                      : ` below $${formatPrice(betDetails.predicted_price)}`}
                  </span>
                </div>
                <div className="flex items-center">
                  <MinusIcon className="h-4 w-4 text-blue-400 mr-1" />
                  <span>
                    Draw if the final price is between 
                    ${formatPrice(Math.min(betDetails.current_price || 0, betDetails.predicted_price || 0))}
                    and 
                    ${formatPrice(Math.max(betDetails.current_price || 0, betDetails.predicted_price || 0))}
                  </span>
                </div>
              </div>
            )}

            {/* Result Box */}
            {status === 'settled' ? (
              <div className={`rounded-lg p-4 ${
                result === 'win'
                  ? 'bg-gradient-to-br from-green-500/20 to-green-500/5 border border-green-500/30'
                  : result === 'lose'
                    ? 'bg-gradient-to-br from-red-500/20 to-red-500/5 border border-red-500/30'
                    : 'bg-gradient-to-br from-blue-500/20 to-blue-500/5 border border-blue-500/30'
              }`}>
                <div className="flex justify-between items-center">
                  <span className="font-medium">
                    {result === 'win' 
                      ? 'Winnings' 
                      : result === 'lose' 
                        ? 'Lost Amount'
                        : 'Returned Amount'
                    }
                  </span>
                  <span className={`text-xl font-bold ${
                    result === 'win' 
                      ? 'text-green-400' 
                      : result === 'lose'
                        ? 'text-red-400'
                        : 'text-blue-400'
                  }`}>
                    {result === 'win' 
                      ? `+${((betDetails.amount || 0) * (betDetails.odds || 0) - (betDetails.amount || 0)).toFixed(0)} CHIPS` 
                      : result === 'lose'
                        ? `-${betDetails.amount || 0} CHIPS`
                        : `${betDetails.amount || 0} CHIPS`
                    }
                  </span>
                </div>
              </div>
            ) : (
              <div className="rounded-lg p-4 bg-gradient-to-br from-violet-500/20 to-violet-500/5 border border-violet-500/30">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Potential Win</span>
                  <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-teal-400">
                    +{((betDetails.amount || 0) * (betDetails.odds || 0) - (betDetails.amount || 0)).toFixed(0)} CHIPS
                  </span>
                </div>
              </div>
            )}

            {/* {status === 'pending' && (
              <motion.button
                onClick={async () => {
                  try {
                    setLoading(true);
                    // Force a fresh fetch of the bet which should trigger settlement
                    const response = await api.get(`/bets/${betId}?force_settle=true`);
                    const payload = response.data;
                    
                    setStatus(payload.status);
                    setResult(payload.result);
                    setChips(payload.remainingChips);
                    setBetDetails(payload.bet ?? payload.data ?? payload);
                    
                    if (payload.status === 'settled' && payload.finalPrice) {
                      setActualPrice(payload.finalPrice);
                    }
                  } catch (err) {
                    console.error("❌ [manualSettle] error:", err);
                    alert("Failed to settle bet: " + (err.response?.data?.message || err.message));
                  } finally {
                    setLoading(false);
                  }
                }}
                className="col-span-2 py-3 sm:py-4 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white font-medium rounded-xl flex items-center justify-center transition shadow-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-200 text-sm sm:text-base">
                  Force Settlement (Testing)
                </span>
              </motion.button>
            )} */}
          </div>
        </div>
        
        {/* Balance Card */}
        <div className="p-1 rounded-lg bg-gradient-to-br from-gray-900 to-black border border-gray-700 shadow-xl mb-6">
          <div className="p-3 sm:p-4 bg-gradient-to-br from-gray-900/90 to-black/90 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-gradient-to-r from-violet-500/30 to-pink-500/30 mr-3">
                <BanknotesIcon className="h-5 w-5 text-pink-400" />
              </div>
              <div>
                <div className="text-xs text-gray-400">
                  {status === 'pending' ? 'Current Balance' : 'New Balance'}
                </div>
                <div className="font-bold text-xl text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-pink-400">
                  {chips.toLocaleString()} <span className="text-xs">CHIPS</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-2 gap-3">
          <motion.button
            onClick={() => navigate('/my-bets')}
            className="py-3 sm:py-4 bg-gradient-to-r from-violet-600 to-pink-600 text-white font-medium rounded-xl flex items-center justify-center transition shadow-lg"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-200 text-sm sm:text-base">
              My Bets
            </span>
            <ChevronRightIcon className="h-4 w-4 ml-1 text-white" />
          </motion.button>
          
          <motion.button
            onClick={() => navigate('/')}
            className="py-3 sm:py-4 bg-gradient-to-br from-gray-800 to-gray-900 text-white font-medium rounded-xl flex items-center justify-center transition border border-gray-700 hover:border-pink-500/50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-300 to-white text-sm sm:text-base">
              All Markets
            </span>
          </motion.button>
        </div>
      </div>
    </main>
  );
}

// Helper function to calculate the position of the actual price marker on the progress bar
function calculatePositionPercentage(actualPrice, entryPrice, predictedPrice) {
  // If prices are the same, avoid division by zero
  if (entryPrice === predictedPrice) return 50;
  
  // Calculate percentage position
  const range = Math.abs(predictedPrice - entryPrice);
  const distance = Math.abs(actualPrice - Math.min(entryPrice, predictedPrice));
  
  // Convert to percentage (0-100)
  let percentage = (distance / range) * 100;
  
  // If predicted price is less than entry price, invert the percentage
  // This is because the progress bar direction is different
  if (predictedPrice < entryPrice) {
    percentage = 100 - percentage;
  }
  
  // Clamp the value between 0 and 100
  return Math.max(0, Math.min(100, percentage));
}
